import axios from 'axios';
import { AnyAction, combineReducers } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import config from 'config';
import { createAction } from 'ducks/actionHelpers';
import { addNotification } from 'ducks/client/notification';
import { ReduxState } from 'ducks';

// Types
export interface GuestAuthData {
  arrival_date: string;
  departure_date: string;
  guest_given_name?: string;
  guest_surname?: string;
}

// Action Types
const AUTHENTICATE_GUEST_REQUEST = 'AUTHENTICATE_GUEST_REQUEST';
const AUTHENTICATE_GUEST_SUCCESS = 'AUTHENTICATE_GUEST_SUCCESS';
const AUTHENTICATE_GUEST_FAILURE = 'AUTHENTICATE_GUEST_FAILURE';

// Action Creators
const authenticateGuestRequest = () => createAction(AUTHENTICATE_GUEST_REQUEST);
const authenticateGuestSuccess = (response: GuestAuthData) =>
  createAction(AUTHENTICATE_GUEST_SUCCESS, response);
const authenticateGuestFailure = (err: string) => createAction(AUTHENTICATE_GUEST_FAILURE, err);

export const authenticateGuest = (
  apiKey: string,
  lastName: string,
  roomNumber: string,
  guestPortalId: string
) => (
  dispatch: ThunkDispatch<Record<string, unknown>, Record<string, unknown>, AnyAction>
): Promise<any> => {
  dispatch(authenticateGuestRequest());

  return axios
    .post(
      `${config.apiUrl}/guestportals/authenticateguest`,
      {
        id: guestPortalId,
        guest_surname: lastName,
        room_no: roomNumber,
      },
      {
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      }
    )
    .then((response) => {
      // Use the API response data which should contain properly formatted data
      const authData = {
        ...response.data,
        // Don't override with user input - use API response data
      };

      dispatch(authenticateGuestSuccess(authData));
      return authData;
    })
    .catch((error) => {
      let errorMessage = 'Authentication failed';

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        if (status === 400) {
          errorMessage = 'Please check your input and try again.';
        } else if (status === 401) {
          errorMessage = 'Invalid last name or room number. Please try again.';
        } else if (status === 403) {
          errorMessage = 'You can only access the guest portal during your stay dates.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage =
            error.response.data?.message || `Error ${status}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Other error
        errorMessage = error.message || 'Authentication failed';
      }

      dispatch(authenticateGuestFailure(errorMessage));
      // Don't throw error - let Redux state handle it
      return Promise.resolve();
    });
};

type Action =
  | ReturnType<typeof authenticateGuestRequest>
  | ReturnType<typeof authenticateGuestSuccess>
  | ReturnType<typeof authenticateGuestFailure>
  | ReturnType<typeof addNotification>;

// Reducers
const error = (state = '', action: Action) => {
  switch (action.type) {
    case AUTHENTICATE_GUEST_FAILURE:
      return action.payload;
    case AUTHENTICATE_GUEST_REQUEST:
    case AUTHENTICATE_GUEST_SUCCESS:
      return '';
    default:
      return state;
  }
};

const loading = (state = false, action: Action) => {
  switch (action.type) {
    case AUTHENTICATE_GUEST_REQUEST:
      return true;
    case AUTHENTICATE_GUEST_SUCCESS:
    case AUTHENTICATE_GUEST_FAILURE:
      return false;
    default:
      return state;
  }
};

const data = (state: GuestAuthData | null = null, action: Action) => {
  switch (action.type) {
    case AUTHENTICATE_GUEST_SUCCESS:
      return action.payload;
    case AUTHENTICATE_GUEST_FAILURE:
      return null;
    default:
      return state;
  }
};

export interface GuestPortalClientState {
  error: ReturnType<typeof error>;
  loading: ReturnType<typeof loading>;
  data: GuestAuthData | null;
}

// Selectors
export const selectGuestPortalClientError = (state: ReduxState) => state.guestPortals?.error || '';
export const selectGuestPortalClientLoading = (state: ReduxState) =>
  state.guestPortals?.loading || false;
export const selectGuestPortalClientData = (state: ReduxState): GuestAuthData | null =>
  state.guestPortals?.data || null;

export default combineReducers({
  error,
  loading,
  data,
});
